import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { get } from "../api";
import TrackedList, { StatusPill, useListFilters } from "../TrackedList";
import type { SuggestedItem } from "../api";

/**
 * Two lists that are deliberately not one.
 *
 * **Tracked** are rows somebody owns: a status, a severity, an assignee, an
 * ETA, a comment thread. **Suggested** is what the agent put in the latest
 * reports and nobody has acted on -- model output, rewritten every run.
 * Merging them would mean a report could silently assign work, and a
 * suggestion the agent stopped repeating would take a real commitment with
 * it when it went.
 *
 * This is the across-every-customer view. One customer's own tracked items
 * live on their page, under the Action items tab.
 */
type Tab = "tracked" | "suggested";

export default function Actions() {
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get("tab") === "suggested" ? "suggested" : "tracked";
  const filters = useListFilters(params, (p) => setParams(p, { replace: true }));

  const setTab = (t: Tab) => {
    const next = new URLSearchParams(params);
    next.set("tab", t);
    setParams(next, { replace: true });
  };

  return (
    <div className="wrap">
      <h1>Action items</h1>

      <div className="tabs">
        <button className={tab === "tracked" ? "tab on" : "tab"} onClick={() => setTab("tracked")}>
          Tracked
        </button>
        <button
          className={tab === "suggested" ? "tab on" : "tab"}
          onClick={() => setTab("suggested")}
        >
          Suggested by reports
        </button>
      </div>

      {tab === "tracked" ? (
        <>
          <p className="sub">
            Work somebody owns, across your customers. Resolved items stay —
            they are the record of what was done.
          </p>
          <TrackedList {...filters} />
        </>
      ) : (
        <Suggested />
      )}
    </div>
  );
}

// --- Suggested ----------------------------------------------------------

function Suggested() {
  const [items, setItems] = useState<SuggestedItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void get<{ items: SuggestedItem[] }>("/api/action-items")
      .then((d) => setItems(d.items))
      .catch((e) => setError(e.message));
  }, []);

  if (error) return <div className="error">{error}</div>;
  if (!items) return <div className="empty">Loading…</div>;

  return (
    <>
      <p className="sub">
        Still open in each customer's most recent report. These are the
        agent's opinion until somebody tracks one — nothing here is assigned
        to anyone.
      </p>

      {items.length === 0 ? (
        <div className="empty">Nothing open.</div>
      ) : (
        <table style={{ marginTop: 14 }}>
          <thead>
            <tr>
              <th>Priority</th>
              <th>Customer</th>
              <th>Item</th>
              <th>Owner</th>
              <th>Due</th>
              <th>Tracked</th>
            </tr>
          </thead>
          <tbody>
            {items.map((i) => (
              <tr key={`${i.report_id}-${i.action_id}`}>
                <td>
                  <span className={`badge ${i.priority ?? "low"}`}>{i.priority}</span>
                </td>
                <td>
                  <Link to={`/c/${i.customer}`}>{i.customer}</Link>
                </td>
                <td>
                  <strong>{i.title}</strong>
                  {i.detail && <div className="dim">{i.detail}</div>}
                </td>
                <td>{i.owner ?? "—"}</td>
                <td>{i.due ?? "—"}</td>
                <td>
                  {i.tracked_id ? (
                    <Link to={`/actions/${i.tracked_id}`}>
                      {i.tracked_status ? (
                        <StatusPill status={i.tracked_status} />
                      ) : (
                        "Tracked →"
                      )}
                    </Link>
                  ) : (
                    <Link className="btn tiny" to={`/c/${i.customer}/report?r=${i.report_id}`}>
                      Track →
                    </Link>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
